import React, { useEffect, useState } from "react";
import axios from "axios";
import { FaUsers, FaPlus, FaTrash } from "react-icons/fa";
import { SERVER_URL } from "../../config";
import { Field, inputClass, SearchableSelect } from "./Shared";
import {
    currencyOptions,
    licenceOptions,
    statusOptions,
    subscriptionMonthOptions,
    computeTotalAmount,
    formatMoney,
    EMPTY_USER_ENTRY,
} from "./utils";

const SectionTitle = ({ children, right }) => (
    <div className="flex items-center justify-between mb-3 pb-2 border-b border-slate-200">
        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-600">{children}</h3>
        {right}
    </div>
);

const UserFormModal = ({
    open,
    editingUser,
    selectedServer,
    servers = [],
    form,
    setForm,
    formError,
    saving,
    onCancel,
    onSubmit,
}) => {
    const [rateLoadingIdx, setRateLoadingIdx] = useState(null);
    const [rateNotices, setRateNotices] = useState({});
    const [employees, setEmployees] = useState([]);
    const [regions, setRegions] = useState([]);

    const users = form.users && form.users.length ? form.users : [{ ...EMPTY_USER_ENTRY }];

    const activeServer =
        servers.find((s) => String(s.server_port) === String(form.server_port)) || selectedServer;

    // Sell person / region dropdown data
    useEffect(() => {
        if (!open) return;
        axios
            .get(`${SERVER_URL}/Cloud/getEmployees.php`)
            .then((res) => {
                if (res.data.success) {
                    setEmployees(res.data.employees || []);
                    setRegions(res.data.regions || []);
                }
            })
            .catch(() => {});
    }, [open]);

    // Reset notices every time the modal opens
    useEffect(() => {
        if (open) { setRateNotices({}); setRateLoadingIdx(null); }
    }, [open]);

    if (!open) return null;

    const sellPersonOptions = employees.map((e) => ({ label: e.label, value: e.employee_id }));
    const regionOptions = regions.map((r) => ({ label: r, value: r }));
    const serverOptions = servers.map((s) => ({
        label: `${s.server_name} (${s.server_port})`,
        value: String(s.server_port),
    }));

    /* ---------- company-level handlers ---------- */
    const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

    const onServerChange = (portValue) => {
        const chosen = servers.find((s) => String(s.server_port) === String(portValue));
        setForm((prev) => ({
            ...prev,
            server_port: portValue,
            server_name: chosen ? chosen.server_name : prev.server_name,
        }));
    };

    const onSellPersonChange = (employeeId) => {
        const emp = employees.find((e) => e.employee_id === employeeId);
        setForm((prev) => ({
            ...prev,
            sell_person_id: employeeId,
            sell_person_name: emp ? emp.employee_name : "",
            region: prev.region || (emp ? emp.region || "" : ""),
        }));
    };

    /* ---------- per-user handlers ---------- */
    const setUserField = (idx, key, value) =>
        setForm((prev) => ({
            ...prev,
            users: (prev.users || []).map((u, i) => (i === idx ? { ...u, [key]: value } : u)),
        }));

    const addUserBlock = () =>
        setForm((prev) => ({ ...prev, users: [...(prev.users || []), { ...EMPTY_USER_ENTRY }] }));

    const removeUserBlock = (idx) =>
        setForm((prev) => ({ ...prev, users: (prev.users || []).filter((_, i) => i !== idx) }));

    // Rate is fetched ONLY when the person changes the licence type on a NEW
    // user. It never runs when an existing user is opened for editing, so the
    // saved rate / currency from the database is never overwritten.
    const onLicenceChange = async (idx, licence) => {
        setUserField(idx, "licence_type", licence);
        if (editingUser) return;
        const provider = activeServer?.provider_name;
        if (!provider || !licence) return;

        setRateLoadingIdx(idx);
        setRateNotices((n) => ({ ...n, [idx]: "" }));
        try {
            const res = await axios.get(`${SERVER_URL}/Cloud/getProviderRate.php`, {
                params: { provider_name: provider, licence_type: licence },
            });
            if (res.data.success) {
                setForm((prev) => ({
                    ...prev,
                    users: (prev.users || []).map((u, i) =>
                        i === idx
                            ? { ...u, rate: res.data.data.rate, currency: u.currency || res.data.data.currency }
                            : u
                    ),
                }));
                setRateNotices((n) => ({ ...n, [idx]: `Rate auto-filled from ${provider}'s rate card. You can change it.` }));
            } else {
                setRateNotices((n) => ({ ...n, [idx]: res.data.message || "No rate card found — enter the rate manually." }));
            }
        } catch {
            setRateNotices((n) => ({ ...n, [idx]: "Could not fetch rate — enter it manually." }));
        } finally {
            setRateLoadingIdx(null);
        }
    };

    const grandTotal = users.reduce(
        (sum, u) => sum + computeTotalAmount(u.rate, u.subscription, u.discount, 1),
        0
    );
    const grandCurrency = users.find((u) => u.currency)?.currency || "INR";

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
            <div
                className="w-full max-w-4xl max-h-[92vh] flex flex-col rounded-xl bg-white border border-slate-200"
                style={{ boxShadow: "0 10px 30px rgba(0,0,0,0.15)" }}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                    <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                        <FaUsers className="w-5 h-5 text-blue-600" />
                        {editingUser ? "Edit User" : "Add New User"}
                        {selectedServer ? ` — ${selectedServer.server_name}` : ""}
                    </h2>
                    <button onClick={onCancel} className="text-slate-400 hover:text-slate-600 text-lg leading-none">✕</button>
                </div>

                {/* Scrollable body */}
                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
                    {formError && (
                        <p className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">{formError}</p>
                    )}

                    {!editingUser && (
                        <p className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                            User ID is generated automatically once you save. Use <b>Add User</b> below to create several users for the same company in one go.
                        </p>
                    )}

                    {/* ===== COMPANY DETAILS ===== */}
                    <section>
                        <SectionTitle>Company Details</SectionTitle>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Field label="Company Name" required>
                                <input type="text" value={form.company_name}
                                    onChange={(e) => setField("company_name", e.target.value)}
                                    className={inputClass} placeholder="Enter company name" />
                            </Field>

                            <Field label="Contact Person Name">
                                <input type="text" value={form.contact_person_name}
                                    onChange={(e) => setField("contact_person_name", e.target.value)}
                                    className={inputClass} placeholder="Contact person name" />
                            </Field>

                            <Field label="Contact Number">
                                <input type="text" value={form.contact_person_number}
                                    onChange={(e) => setField("contact_person_number", e.target.value)}
                                    className={inputClass} placeholder="Contact number" />
                            </Field>

                            <Field label="Server" required hint="Search by server name or port">
                                <SearchableSelect options={serverOptions} value={form.server_port}
                                    onChange={onServerChange} placeholder="Search server…" />
                            </Field>

                            <Field label="Sell Person" hint="Search by name or employee ID">
                                <SearchableSelect options={sellPersonOptions} value={form.sell_person_id}
                                    onChange={onSellPersonChange} placeholder="Search employee…" />
                            </Field>

                            <Field label="Region">
                                <SearchableSelect options={regionOptions} value={form.region}
                                    onChange={(v) => setField("region", v)} placeholder="Search region…" allowCustom />
                            </Field>

                            <Field label="Referred Person">
                                <input type="text" value={form.referred_person}
                                    onChange={(e) => setField("referred_person", e.target.value)}
                                    className={inputClass} placeholder="Referred person name" />
                            </Field>

                            <Field label="Referred Person Number">
                                <input type="text" value={form.referred_person_number}
                                    onChange={(e) => setField("referred_person_number", e.target.value)}
                                    className={inputClass} placeholder="Referred person number" />
                            </Field>

                            <Field label="Status">
                                <select value={form.status} onChange={(e) => setField("status", e.target.value)} className={inputClass}>
                                    {statusOptions.map((opt) => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </Field>

                            <div className="hidden md:block" />

                            <Field label="Remarks" className="col-span-1 md:col-span-2">
                                <textarea value={form.remarks} onChange={(e) => setField("remarks", e.target.value)}
                                    className={inputClass} rows="2" placeholder="Additional remarks" />
                            </Field>

                            <Field label="Reason" className="col-span-1 md:col-span-2">
                                <textarea value={form.reason} onChange={(e) => setField("reason", e.target.value)}
                                    className={inputClass} rows="2" placeholder="Reason for status change" />
                            </Field>
                        </div>
                    </section>

                    {/* ===== USER DETAILS (repeatable) ===== */}
                    <section>
                        <SectionTitle
                            right={
                                !editingUser && (
                                    <button
                                        type="button"
                                        onClick={addUserBlock}
                                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-emerald-700 border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 transition-colors"
                                    >
                                        <FaPlus className="w-3 h-3" /> Add User
                                    </button>
                                )
                            }
                        >
                            User Details {!editingUser && `(${users.length})`}
                        </SectionTitle>

                        <div className="space-y-4">
                            {users.map((u, idx) => {
                                const rowTotal = computeTotalAmount(u.rate, u.subscription, u.discount, 1);
                                return (
                                    <div key={idx} className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
                                        <div className="flex items-center justify-between mb-3">
                                            <span className="text-xs font-bold text-blue-700 bg-blue-100 px-2.5 py-1 rounded-full">
                                                {editingUser ? "User" : `User ${idx + 1}`}
                                            </span>
                                            {!editingUser && users.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => removeUserBlock(idx)}
                                                    className="flex items-center gap-1 text-xs font-semibold text-rose-600 hover:text-rose-700"
                                                >
                                                    <FaTrash className="w-3 h-3" /> Remove
                                                </button>
                                            )}
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <Field label="Username" required>
                                                <input type="text" value={u.user_name}
                                                    onChange={(e) => setUserField(idx, "user_name", e.target.value)}
                                                    className={inputClass} placeholder="Enter username" />
                                            </Field>

                                            <Field label="Password" required>
                                                <input type="text" value={u.user_password}
                                                    onChange={(e) => setUserField(idx, "user_password", e.target.value)}
                                                    className={inputClass} placeholder="Enter password" autoComplete="off" />
                                            </Field>

                                            <Field label="Licence Type">
                                                <select value={u.licence_type}
                                                    onChange={(e) => onLicenceChange(idx, e.target.value)}
                                                    className={inputClass}>
                                                    <option value="">Select Licence Type</option>
                                                    {licenceOptions.map((opt) => (
                                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                    ))}
                                                </select>
                                            </Field>

                                            <Field label="Gateway Path">
                                                <input type="text" value={u.gateway_path}
                                                    onChange={(e) => setUserField(idx, "gateway_path", e.target.value)}
                                                    className={inputClass} placeholder="Gateway path" />
                                            </Field>

                                            {u.licence_type === "shared" && (
                                                <>
                                                    <Field label="Actual Username">
                                                        <input type="text" value={u.actual_user_name}
                                                            onChange={(e) => setUserField(idx, "actual_user_name", e.target.value)}
                                                            className={inputClass} placeholder="Actual username" />
                                                    </Field>
                                                    <Field label="Actual Password">
                                                        <input type="text" value={u.actual_user_password}
                                                            onChange={(e) => setUserField(idx, "actual_user_password", e.target.value)}
                                                            className={inputClass} placeholder="Actual password" autoComplete="off" />
                                                    </Field>
                                                </>
                                            )}

                                            <Field label="Activation Date">
                                                <input type="date" value={u.activation_date || ""}
                                                    onChange={(e) => setUserField(idx, "activation_date", e.target.value)}
                                                    className={inputClass} />
                                            </Field>

                                            <Field label="Expire Date">
                                                <input type="date" value={u.expire_date || ""}
                                                    onChange={(e) => setUserField(idx, "expire_date", e.target.value)}
                                                    className={inputClass} />
                                            </Field>

                                            <Field label="Next Renewal Date">
                                                <input type="date" value={u.next_renewal_date || ""}
                                                    onChange={(e) => setUserField(idx, "next_renewal_date", e.target.value)}
                                                    className={inputClass} />
                                            </Field>

                                            <Field label="Prorata Expire Date">
                                                <input type="date" value={u.prorata_expire_date || ""}
                                                    onChange={(e) => setUserField(idx, "prorata_expire_date", e.target.value)}
                                                    className={inputClass} />
                                            </Field>

                                            <Field label="Rate" hint={rateLoadingIdx === idx ? "Fetching rate…" : rateNotices[idx]}>
                                                <input type="number" value={u.rate}
                                                    onChange={(e) => setUserField(idx, "rate", e.target.value)}
                                                    className={inputClass} placeholder="Rate amount" />
                                            </Field>

                                            <Field label="Discount">
                                                <input type="number" value={u.discount}
                                                    onChange={(e) => setUserField(idx, "discount", e.target.value)}
                                                    className={inputClass} placeholder="0" />
                                            </Field>

                                            <Field label="Currency">
                                                <select value={u.currency}
                                                    onChange={(e) => setUserField(idx, "currency", e.target.value)}
                                                    className={inputClass}>
                                                    <option value="">Select Currency</option>
                                                    {currencyOptions.map((opt) => (
                                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                    ))}
                                                </select>
                                            </Field>

                                            <Field label="Subscription">
                                                <SearchableSelect options={subscriptionMonthOptions} value={u.subscription}
                                                    onChange={(v) => setUserField(idx, "subscription", v)}
                                                    placeholder="Search months…" />
                                            </Field>
                                        </div>

                                        <div className="mt-3 flex items-center justify-between rounded-lg bg-white border border-slate-200 px-3 py-2">
                                            <span className="text-[11px] text-slate-500">rate × subscription − discount</span>
                                            <span className="text-sm font-bold text-blue-800">{formatMoney(rowTotal, u.currency || "INR")}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {!editingUser && (
                            <button
                                type="button"
                                onClick={addUserBlock}
                                className="mt-4 w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 border-dashed border-emerald-300 text-sm font-semibold text-emerald-700 hover:bg-emerald-50 transition-colors"
                            >
                                <FaPlus className="w-3.5 h-3.5" /> Add another user
                            </button>
                        )}
                    </section>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-slate-200 bg-white rounded-b-xl">
                    <div className="mb-3 rounded-lg bg-blue-50 border border-blue-200 px-4 py-2.5 flex items-center justify-between">
                        <div className="text-xs font-semibold text-blue-700">
                            Total Amount {users.length > 1 ? `(${users.length} users)` : ""}
                        </div>
                        <div className="text-lg font-extrabold text-blue-800">{formatMoney(grandTotal, grandCurrency)}</div>
                    </div>
                    <div className="flex justify-end gap-2">
                        <button onClick={onCancel}
                            className="px-4 py-2 rounded-lg text-sm border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors">
                            Cancel
                        </button>
                        <button onClick={onSubmit} disabled={saving}
                            className="px-4 py-2 rounded-lg text-sm text-white disabled:opacity-60 transition-opacity"
                            style={{ background: "linear-gradient(90deg, #1e40af, #0369a1)" }}>
                            {saving ? "Saving…" : editingUser ? "Update" : users.length > 1 ? `Save ${users.length} Users` : "Save"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserFormModal;